import { Map } from "immutable"

import {
    STUDIO_EDIT_APPLY_MODEL_UPDATE as APPLY_MODEL_UPDATE,
    STUDIO_ITEM_CACHED_RESPONSE as ITEM_CACHED_RESPONSE,
    STUDIO_ITEM_DELETE_ERROR_RESPONSE as ITEM_DELETE_ERROR_RESPONSE,
    STUDIO_ITEM_DELETE_REQUEST as ITEM_DELETE_REQUEST,
    STUDIO_ITEM_DELETE_RESPONSE as ITEM_DELETE_RESPONSE,
    STUDIO_ITEM_ERROR_RESPONSE as ITEM_ERROR_RESPONSE,
    STUDIO_ITEM_REQUEST as ITEM_REQUEST,
    STUDIO_ITEM_RESPONSE as ITEM_RESPONSE,
    STUDIO_ITEM_SAVE_RESPONSE as ITEM_SAVE_RESPONSE,
    STUDIO_ITEM_SAVE_ERROR_RESPONSE as ITEM_SAVE_ERROR_RESPONSE,
    STUDIO_LIST_CACHED_RESPONSE as LIST_CACHED_RESPONSE,
    STUDIO_LIST_ERROR_RESPONSE as LIST_ERROR_RESPONSE,
    STUDIO_LIST_REQUEST as LIST_PAGE_REQUEST,
    STUDIO_LIST_RESPONSE as LIST_RESPONSE,
    STUDIO_LIST_SEARCH_REQUEST as LIST_SEARCH_REQUEST,
    STUDIO_CLONE_MAP,
    STUDIO_DIMENSION_ITEM_REQUEST,
    STUDIO_DIMENSION_ITEM_RESPONSE,
    STUDIO_EDIT_COPY_IMAGE_URL,
    STUDIO_STAKEHOLDERS_LIST_REQUEST,
    STUDIO_STAKEHOLDERS_LIST_RESPONSE,
    STUDIO_HIGHLIGHT_DIMENSION,
    STUDIO_HIGHLIGHT_TOPIC,
    STUDIO_KNOWLEDGE_ITEM_REQUEST,
    STUDIO_KNOWLEDGE_ITEM_RESPONSE,
    STUDIO_KNOWLEDGE_LIST_REQUEST,
    STUDIO_KNOWLEDGE_LIST_RESPONSE,
    STUDIO_LIST_TYPE_CHANGE,
    STUDIO_NEW_MAP,
    STUDIO_PROJECTS_LIST_REQUEST,
    STUDIO_PROJECTS_LIST_RESPONSE,
    STUDIO_REPORTS_LIST_REQUEST,
    STUDIO_REPORTS_LIST_RESPONSE,
    STUDIO_KNOWLEDGE_DATA_LIST_REQUEST,
    STUDIO_KNOWLEDGE_DATA_LIST_RESPONSE,
    STUDIO_SESSIONS_LIST_REQUEST,
    STUDIO_SESSIONS_LIST_RESPONSE,
    TOPIC_SELECTED_AS_EVIDENCE,
    TOPIC_TIMESTAMP_SELECTED,
    ADVANCED_MODE_CLOSED,
    TOGGLE_ADVANCED_MODE_MODERATION,
    STUDIO_EDIT_UPDATE_IMAGE as UPDATE_IMAGE,
    STUDIO_EDIT_UPDATE_MODEL as UPDATE_MODEL,
    STUDIO_SHOW_INTERACTIVE_DATA,
    STUDIO_HIDE_INTERACTIVE_DATA
} from "../../actions/action-types"
import { MapModel, updateMapChangeModel } from "../../models/entities/map"
import {
    StudioFilterModel,
    updateStudioSearchFilterModel,
    defaultStudioSearchFilterModel
} from "../../models/filters/studio-filter"
import { defaultAllTopicTypes } from "../../config/application-definitions"
import { serialiseSearchCriteria } from "../paging/paging-reducer-utils"
import PresentationReducerBuilder from "./build-presentation-reducer"

const reducerBuilder = new PresentationReducerBuilder()
const defaultReducer = reducerBuilder
    .actionsToHandle({
        LIST_PAGE_REQUEST,
        LIST_SEARCH_REQUEST,
        LIST_RESPONSE,
        LIST_CACHED_RESPONSE,
        LIST_ERROR_RESPONSE,
        ITEM_RESPONSE,
        ITEM_CACHED_RESPONSE,
        ITEM_ERROR_RESPONSE,
        UPDATE_MODEL,
        UPDATE_IMAGE,
        APPLY_MODEL_UPDATE,
        ITEM_DELETE_REQUEST,
        ITEM_DELETE_RESPONSE,
        ITEM_DELETE_ERROR_RESPONSE,
        ITEM_SAVE_RESPONSE,
        ITEM_SAVE_ERROR_RESPONSE
    })
    .updateChangeModel(updateMapChangeModel)
    .workingModelClass(MapModel)
    .canSave(
        (updateModel, _, workingModel) =>
            workingModel.name.length > 0 &&
            workingModel.status.length > 0 &&
            (workingModel.owners.length > 0 ||
                workingModel.nonEditOwners.length > 0)
    )
    .workingFilterModelClass(StudioFilterModel)
    .updateSearchFilterModel(updateStudioSearchFilterModel)
    .defaultState({
        searchCriteria: serialiseSearchCriteria(defaultStudioSearchFilterModel),
        workingFilterModel: new StudioFilterModel(),
        searchFilterModel: defaultStudioSearchFilterModel,
        workingModel: new MapModel(),
        highlightedId: null,
        highlightedType: null,
        isClonedMap: false,
        canOpenSave: false,
        imageCopyURL: undefined,
        isFetchingDimension: false,
        knowledgeSearchCriteria: "",
        isFetchingKnowledgeList: false,
        isFetchingKnowledgeItems: {},
        stakeholdersSearchCriteria: "",
        isFetchingStakeholdersList: false,
        sessionsSearchCriteria: "",
        isFetchingSessionsList: false,
        projectsSearchCriteria: "",
        isFetchingProjectsList: false,
        reportsSearchCriteria: "",
        isFetchingReportsList: false,
        knowledgeDataSearchCriteria: "",
        isFetchingKnowledgeDataList: false,
        advancedModeModeration: true,
        isShowingInteractiveData: false,
        interactiveLink: null
    })
    .build()

const canOpenSave = workingModel => workingModel.name.length > 0

export default (state = undefined, action) => {
    const newDefaultState = defaultReducer(state, action)
    const {
        id,
        data,
        filteredRole,
        isInvalidating,
        type,
        isViewingTopic,
        highlightedId,
        publicationType,
        payload,
        searchCriteria
    } = action
    switch (type) {
        case STUDIO_EDIT_COPY_IMAGE_URL:
            const { imageUrl } = action
            return {
                ...newDefaultState,
                workingModel: newDefaultState.workingModel.set(
                    "image_url",
                    imageUrl
                ),
                imageCopyURL: imageUrl
            }
        case STUDIO_LIST_TYPE_CHANGE: {
            const { topicType } = action
            let valueType = topicType
            if (topicType === "")
                valueType = defaultAllTopicTypes.map(({ name }) => name)

            let newSearchFilterModel = newDefaultState.searchFilterModel.set(
                "types",
                valueType
            )
            let newWorkingFilterModel = newDefaultState.workingFilterModel.set(
                "types",
                topicType
            )
            if (topicType === "Custom Map") {
                newSearchFilterModel = newSearchFilterModel.set("statuses", [
                    "Active",
                    "Draft",
                    "Idea"
                ])
                newWorkingFilterModel = newWorkingFilterModel.set("statuses", [
                    "Active",
                    "Draft",
                    "Idea"
                ])
            } else {
                newSearchFilterModel = newSearchFilterModel.set(
                    "statuses",
                    "Active"
                )
                newWorkingFilterModel = newWorkingFilterModel.set(
                    "statuses",
                    "Active"
                )
            }
            return {
                ...newDefaultState,
                workingFilterModel: newWorkingFilterModel,
                searchFilterModel: newSearchFilterModel,
                searchCriteria: serialiseSearchCriteria(newSearchFilterModel)
            }
        }
        case LIST_PAGE_REQUEST:
            return {
                ...newDefaultState,
                searchCriteria: serialiseSearchCriteria(searchCriteria)
            }
        case UPDATE_MODEL:
            return {
                ...newDefaultState,
                canOpenSave: canOpenSave(newDefaultState.workingModel)
            }
        case UPDATE_IMAGE:
            return {
                ...newDefaultState,
                workingModel: newDefaultState.workingModel.set(
                    "image_url",
                    newDefaultState.workingModel.imagePreviewURL
                )
            }
        case STUDIO_NEW_MAP:
            const newWorkingModel = new MapModel()
            return {
                ...newDefaultState,
                activeId: "",
                workingModel: newWorkingModel,
                updateModel: new Map(),
                canOpenSave: canOpenSave(newWorkingModel)
            }
        case STUDIO_CLONE_MAP:
            return {
                ...newDefaultState,
                isClonedMap: true
            }
        case STUDIO_SHOW_INTERACTIVE_DATA:
            const { interactiveLink } = action
            return {
                ...newDefaultState,
                isShowingInteractiveData: true,
                interactiveLink
            }
        case STUDIO_HIDE_INTERACTIVE_DATA:
            return {
                ...newDefaultState,
                isShowingInteractiveData: false,
                interactiveLink: null
            }
        case STUDIO_DIMENSION_ITEM_REQUEST:
            return {
                ...newDefaultState,
                isFetchingDimension: true
            }
        case STUDIO_DIMENSION_ITEM_RESPONSE:
            return {
                ...newDefaultState,
                isFetchingDimension: false
            }
        case STUDIO_KNOWLEDGE_LIST_REQUEST:
            const entityFilter = isViewingTopic ? "topics" : "dimensions"
            let knowledgeSearchCriteria = Map()
                .set("order", "time")
                .set("approved", true)
                .set(entityFilter, highlightedId)
            if (publicationType) {
                knowledgeSearchCriteria = knowledgeSearchCriteria.set(
                    "source_publication_type",
                    publicationType
                )
            }
            const knowledgeSearchCriteriaString = serialiseSearchCriteria(
                knowledgeSearchCriteria
            )
            return {
                ...newDefaultState,
                isFetchingKnowledgeList: true,
                knowledgeSearchCriteria: knowledgeSearchCriteriaString
            }
        case STUDIO_KNOWLEDGE_LIST_RESPONSE:
            return {
                ...newDefaultState,
                isFetchingKnowledgeList: false
            }
        case STUDIO_KNOWLEDGE_ITEM_REQUEST:
            return {
                ...newDefaultState,
                isFetchingKnowledgeItems: {
                    ...newDefaultState.isFetchingKnowledgeItems,
                    [id]: true
                }
            }
        case STUDIO_KNOWLEDGE_ITEM_RESPONSE:
            return {
                ...newDefaultState,
                isFetchingKnowledgeItems: {
                    ...newDefaultState.isFetchingKnowledgeItems,
                    [data.id]: false
                }
            }
        case STUDIO_STAKEHOLDERS_LIST_REQUEST:
            let stakeholdersSearchCriteria = `${isViewingTopic
                ? "topic"
                : "dimension"}=${highlightedId}&approved=true`
            if (!isViewingTopic)
                stakeholdersSearchCriteria += `&context=${newDefaultState.activeId}`
            if (filteredRole)
                stakeholdersSearchCriteria += `&role=${filteredRole}`
            return {
                ...newDefaultState,
                isFetchingStakeholdersList: true,
                stakeholdersSearchCriteria
            }
        case STUDIO_STAKEHOLDERS_LIST_RESPONSE:
            return {
                ...newDefaultState,
                isFetchingStakeholdersList: false
            }
        case STUDIO_SESSIONS_LIST_REQUEST:
            return {
                ...newDefaultState,
                isFetchingSessionsList: true,
                sessionsSearchCriteria: `${isViewingTopic
                    ? "topic"
                    : "dimension"}=${highlightedId}`
            }
        case STUDIO_SESSIONS_LIST_RESPONSE:
            return {
                ...newDefaultState,
                isFetchingSessionsList: false
            }
        case STUDIO_PROJECTS_LIST_REQUEST:
            return {
                ...newDefaultState,
                isFetchingProjectsList: true,
                projectsSearchCriteria: `${isViewingTopic
                    ? "topic"
                    : "dimension"}=${highlightedId}`
            }
        case STUDIO_PROJECTS_LIST_RESPONSE:
            return {
                ...newDefaultState,
                isFetchingProjectsList: false
            }
        case STUDIO_REPORTS_LIST_REQUEST:
            return {
                ...newDefaultState,
                isFetchingReportsList: true,
                reportsSearchCriteria: `${isViewingTopic
                    ? "topic"
                    : "dimension"}=${highlightedId}&group_by=publisher`
            }
        case STUDIO_REPORTS_LIST_RESPONSE:
            return {
                ...newDefaultState,
                isFetchingReportsList: false
            }
        case STUDIO_KNOWLEDGE_DATA_LIST_REQUEST:
            return {
                ...newDefaultState,
                isFetchingKnowledgeDataList: true,
                knowledgeDataSearchCriteria: `${isViewingTopic
                    ? "topics"
                    : "dimensions"}=${highlightedId}`
            }
        case STUDIO_KNOWLEDGE_DATA_LIST_RESPONSE:
            return {
                ...newDefaultState,
                isFetchingKnowledgeDataList: false
            }
        case ITEM_REQUEST:
            return {
                ...newDefaultState,
                isFetchingItem: true,
                isInvalidating,
                activeId: id,
                knowledgeSearchCriteria: "",
                stakeholdersSearchCriteria: "",
                sessionsSearchCriteria: "",
                projectsSearchCriteria: "",
                reportsSearchCriteria: "",
                interactiveLink: null,
                isShowingInteractiveData: false
            }
        case ITEM_RESPONSE:
        case ITEM_CACHED_RESPONSE:
            return {
                ...newDefaultState,
                isClonedMap: false,
                imageCopyURL: undefined,
                canOpenSave: canOpenSave(newDefaultState.workingModel)
            }
        case STUDIO_HIGHLIGHT_DIMENSION:
            return {
                ...newDefaultState,
                highlightedId: id,
                highlightedType: "dimension"
            }
        case STUDIO_HIGHLIGHT_TOPIC:
            return {
                ...newDefaultState,
                highlightedId: newDefaultState.activeId,
                highlightedType: "topic"
            }
        case TOPIC_TIMESTAMP_SELECTED:
            return {
                ...newDefaultState,
                advancedModeTimestamp: payload
            }
        case ADVANCED_MODE_CLOSED:
            return {
                ...newDefaultState,
                advancedModeModeration: true,
                advancedModeTimestamp: null
            }
        case TOGGLE_ADVANCED_MODE_MODERATION:
            return {
                ...newDefaultState,
                advancedModeModeration: !newDefaultState.advancedModeModeration
            }
        case TOPIC_SELECTED_AS_EVIDENCE:
            return {
                ...newDefaultState,
                topicSelectedAsEvidence: payload
            }
        case "@@router/LOCATION_CHANGE":
            return {
                ...newDefaultState,
                topicSelectedAsEvidence: null
            }
        default:
            return newDefaultState
    }
}
